import { ElementMeasurement } from '../measurementsStore/elementMeasurements/elementMeasurementsTypes';

type PreviousMeasurementsById = Record<string, ElementMeasurement | undefined>;

type PendingBatchCollision = {
    elementIds: string[];
    previousMeasurements: PreviousMeasurementsById;
};

type CollisionDetectionManagerType = {
    enabled: boolean;
    temporarilyEnabled: boolean;
    blocked: boolean;
    logged: boolean;
    pendingBatchCollision: PendingBatchCollision | null;
    cancelPendingBatchCheck: (() => void) | null;
    // Elements that must not push their neighbours while the current collision window lasts
    newElementIdsWithoutCollision: Set<string>;
};

export const collisionDetectionManager: CollisionDetectionManagerType = {
    enabled: false,
    temporarilyEnabled: false,
    // Allows collision detection to be turned off in the rare scenario where it's
    // in an infinite loop
    blocked: false,
    logged: false,
    pendingBatchCollision: null,
    cancelPendingBatchCheck: null,
    newElementIdsWithoutCollision: new Set(),
};

/**
 * An element created while collision detection happens to be on (e.g. a task dragged out of a
 * V2 list, whose source update enables it) would otherwise push its neighbours down, first as a
 * Cmd+Enter style "new card" and again when its editor renders and it grows. Only Cmd+Enter
 * style creates should do that.
 *
 * The exemption lasts until the window closes, because a busy main thread can hold the window
 * open across several of the new element's measurements. With no window open (e.g. an
 * Option-drag copy, which leaves the source untouched) there is nothing to exempt, and recording
 * the ID would carry it into an unrelated later window.
 */
export const skipNewElementCollision = (elementId: string) => {
    if (!collisionDetectionManager.enabled) return;

    collisionDetectionManager.newElementIdsWithoutCollision.add(elementId);
};

export const isNewElementWithoutCollision = (elementId: string) =>
    collisionDetectionManager.newElementIdsWithoutCollision.has(elementId);

export const setPendingBatchCollision = (
    elementIds: string[] = [],
    previousMeasurements: PreviousMeasurementsById = {},
) => {
    collisionDetectionManager.pendingBatchCollision = {
        elementIds,
        previousMeasurements,
    };
};

export const clearPendingBatchCollision = () => {
    collisionDetectionManager.pendingBatchCollision = null;
};

export const isPendingBatchCollisionElementId = (elementId: string) =>
    collisionDetectionManager.pendingBatchCollision?.elementIds?.includes(elementId);

export const getPendingBatchCollision = () => collisionDetectionManager.pendingBatchCollision;

export const setPendingBatchCollisionCancelFn = (cancelFn: () => void) => {
    if (collisionDetectionManager.cancelPendingBatchCheck) {
        collisionDetectionManager.cancelPendingBatchCheck();
    }

    collisionDetectionManager.cancelPendingBatchCheck = cancelFn;
};

export const clearPendingBatchCollisionCancelFn = () => {
    collisionDetectionManager.cancelPendingBatchCheck = null;
};

export const disableCollisionDetection = () => {
    collisionDetectionManager.enabled = false;
    collisionDetectionManager.temporarilyEnabled = false;
    collisionDetectionManager.newElementIdsWithoutCollision.clear();
};

export const enableCollisionDetection = (temporarilyEnabled = false) => {
    collisionDetectionManager.enabled = true;
    collisionDetectionManager.temporarilyEnabled = temporarilyEnabled;
};

/**
 * To prevent an infinite loop bug - this flag will prevent the actual collision moves
 * from being made, but will enable it to be logged until navigations resets the state
 */
export const blockCollisionDetectionUntilNavigation = () => {
    collisionDetectionManager.blocked = true;
    collisionDetectionManager.logged = false;
};

export const unblockCollisionDetectionAfterNavigation = () => {
    collisionDetectionManager.blocked = false;
    collisionDetectionManager.logged = false;
    collisionDetectionManager.newElementIdsWithoutCollision.clear();
    clearPendingBatchCollision();

    if (collisionDetectionManager.cancelPendingBatchCheck) {
        collisionDetectionManager.cancelPendingBatchCheck();
    }

    clearPendingBatchCollisionCancelFn();
};

let timeoutId: NodeJS.Timeout | undefined;

/**
 * Enables collision detection for a single JavaScript event loop cycle,
 * unless it's already enabled, in which case it will simply leave it as enabled.
 */
export const enableCollisionDetectionTemporarily = () => {
    // If already enabled, then don't do so temporarily
    if (collisionDetectionManager.enabled && !collisionDetectionManager.temporarilyEnabled) return;

    // If not enabled, or temporarily enabled - enable collision detection, clear old timers and start a new timer
    enableCollisionDetection(true);

    clearTimeout(timeoutId);

    // Toggle off collision detection in two cycles
    // (Safari requires two for collapsing and expanding columns for some reason)
    timeoutId = setTimeout(() => {
        timeoutId = setTimeout(disableCollisionDetection, 0);
    }, 0);
};
